import { Form, Switch, InputNumber, Popover, Typography, message } from 'antd'
import { Horizontal, Vertical, GraviButton, Texto } from '@gravitate-js/excalibrr'
import _ from 'lodash'
import type { ConfigSwitchProps } from '../utils/configTypes'
import { useConfigInlineEdit } from '../hooks/useConfigInlineEdit'
import { useConfigValidation } from '../hooks/useConfigValidation'
import { formatSwitchValue, getThresholdDescription, getPopoverTitle } from '../utils/configHelpers'

const { Text } = Typography

export function ConfigSwitch({ form, configType, field, alwaysEnabled, onFormChange }: ConfigSwitchProps) {
  const { isPopoverOpen, setIsPopoverOpen, isLoading, handleToggle, handleCancel, handleApply } = useConfigInlineEdit(
    form,
    configType,
    field,
    onFormChange
  )

  const { precision, getValidationRules, normalize, getValueProps, parser, formatter } = useConfigValidation(field)

  if (alwaysEnabled) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Texto weight='bold' className='p-3'>
          ALWAYS
        </Texto>
      </div>
    )
  }

  if (!field.unit) {
    return (
      <div className='config-cell'>
        <Form.Item
          style={{ margin: 0 }}
          shouldUpdate={(prevValues, curValues) => {
            return !_.isEqual(prevValues[configType]?.[field.key], curValues[configType]?.[field.key])
          }}
        >
          {() => (
            <Form.Item name={[configType, field.key, 'enabled']} style={{ margin: 0 }}>
              <Switch checkedChildren='ON' unCheckedChildren='OFF' style={{ width: 80 }} onChange={onFormChange} />
            </Form.Item>
          )}
        </Form.Item>
      </div>
    )
  }

  // For fields with thresholds, we need to ensure the threshold field is always registered
  // even when the popover isn't open, to prevent null values on save
  const hiddenThresholdField = (
    <Form.Item name={[configType, field.key, 'threshold']} hidden rules={getValidationRules(configType)}>
      <InputNumber />
    </Form.Item>
  )

  const switchElement = (
    <Form.Item
      style={{ margin: 0 }}
      shouldUpdate={(prevValues, curValues) => {
        return !_.isEqual(prevValues[configType]?.[field.key], curValues[configType]?.[field.key])
      }}
    >
      {() => {
        // Get values inside the render function to ensure they're current
        const currentFieldValue = form.getFieldValue([configType, field.key])
        const currentThreshold = currentFieldValue?.threshold
        const currentEnabled = currentFieldValue?.enabled

        return (
          <Form.Item name={[configType, field.key, 'enabled']} style={{ margin: 0 }}>
            <Switch
              loading={isLoading || isPopoverOpen}
              checkedChildren={
                currentEnabled && currentThreshold != null ? formatSwitchValue(currentThreshold, field.unit) : 'ON'
              }
              unCheckedChildren='OFF'
              style={{ width: 80 }}
              onChange={handleToggle}
            />
          </Form.Item>
        )
      }}
    </Form.Item>
  )

  return (
    <div className='config-cell'>
      {/* Hidden field to ensure threshold is always in form */}
      {hiddenThresholdField}
      <Popover
        content={
          <Vertical gap={16} style={{ padding: '8px', minWidth: '300px' }}>
            <div>
              <Text type='secondary' style={{ display: 'block', marginBottom: '16px' }}>
                {getThresholdDescription(field.key, configType, field)}
              </Text>
              <label
                htmlFor={`threshold-${field.key}`}
                style={{
                  fontWeight: 500,
                  marginBottom: '8px',
                  display: 'block',
                }}
              >
                Enter threshold value:
              </label>
              {/* Use Form.Item with dependencies to update the hidden field */}
              <Form.Item
                name={[configType, field.key, 'threshold']}
                noStyle
                normalize={normalize}
                getValueProps={getValueProps}
                shouldUpdate={(prevValues, curValues) => {
                  return !_.isEqual(
                    prevValues[configType]?.[field.key]?.threshold,
                    curValues[configType]?.[field.key]?.threshold
                  )
                }}
              >
                <InputNumber
                  id={`threshold-${field.key}`}
                  style={{ width: '100%' }}
                  min={0}
                  max={field.unit === '%' ? 100 : undefined}
                  precision={precision}
                  step={field.unit === '%' ? 0.01 : 1}
                  suffix={field.unit}
                  stringMode
                  parser={parser}
                  formatter={formatter}
                />
              </Form.Item>
            </div>
            <Horizontal justifyContent='flex-end' gap={8}>
              <GraviButton appearance='text' onClick={handleCancel}>
                Cancel
              </GraviButton>
              <GraviButton theme2 type='primary' onClick={handleApply}>
                Apply
              </GraviButton>
            </Horizontal>
          </Vertical>
        }
        title={getPopoverTitle(configType, field)}
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (!open && isPopoverOpen) {
            // User clicked outside, treat as cancel
            handleCancel()
          }
        }}
        trigger='click'
      >
        {switchElement}
      </Popover>
    </div>
  )
}
